import React, { useRef, useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

import { state } from '../store/global';
import API from '../lib/api';
import findMessage from '../utils/messages';
import SectionBanner from '../components/SectionBanner';
import {
  getFlag,
  getRouteName,
  getAccountNumber,
  VirtualAccountCurrencies,
  getVACreationText,
} from '../utils/constants';

import Notification from '../components/common/Notification';
import BankAccountModal from '../components/modal/BankAccountModal';
import IconButton from '../components/common/IconButton';
import NormalButton from '../components/common/NormalButton';

import PlusIcon from '../assets/images/plus-white.svg';
import LogoIcon from '../assets/images/logo_no_letter.svg';
import DotThreeVerticalIcon from '../assets/images/dotsThreeVertical.svg';
import SuccessIcon from '../assets/images/submitted.svg';

export default function Account() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const bankItemRefs = [useRef(null), useRef(null), useRef(null)];
  const [notification, setNotification] = useState(null);
  const [isOpenMenuId, setIsOpenMenuId] = useState(-1);
  const [isOpenBankAccount, setIsOpenBankAccount] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  // Virtual account create modal data
  const [isOpenVAModal, setIsOpenVAModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [balance, setBalance] = useState(0);
  const [createdVA, setCreatedVA] = useState(false);

  const fetchData = async () => {
    await API.get('user/getVirtualAccounts')
      .then((res) => {
        console.log('getVirtualAccounts: ', res);
        setBalance(res.data.data.balance);
        setVirtualAccounts(res.data.data.virtualAccounts);
      })
      .catch((err) => {
        console.log('err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };

  const enableSpei = async () => {
    API.put('user/enableSpei')
      .then((res) => {
        console.log('enable spei: ', res.data);
      })
      .catch((err) => {
        console.log('err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };
  const fetchVirtualAccountsByBridge = async () => {
    await API.get('user/getAllVirtualAccounts')
      .then((res) => {
        console.log('fetchVirtualAccountsByBridge: ', res);
      })
      .catch((err) => {
        console.log('getAllVirtualAccounts err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };
  const activateVirtualAccount = async () => {
    await API.put('user/activateVirtualAccount', {
      virtualAccountId: '17dac478-b4ce-4599-8f53-5745336e616a',
    })
      .then((res) => {
        console.log('activateVirtualAccount: ', res);
      })
      .catch((err) => {
        console.log('activateVirtualAccount err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };
  const deactivateVirtualAccount = async () => {
    await API.put('user/deactivateVirtualAccount', {
      virtualAccountId: '8523cf7b-b107-4bba-a20f-2d5989c20894',
    })
      .then((res) => {
        console.log('deactivateVirtualAccount: ', true);
      })
      .catch((err) => {
        console.log('deactivateVirtualAccount err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };

  useEffect(() => {
    // showNotification('¡La información se copio exitosamente!', 'success');
    fetchData();
    // enableSpei();
    // activateVirtualAccount();
    // deactivateVirtualAccount();
    // fetchVirtualAccountsByBridge();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      bankItemRefs.map((item) => {
        if (item.current && !item.current.contains(event.target)) {
          setIsOpenMenuId(-1);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const openModal = (idx) => {
    setSelectedBank(virtualAccounts[idx]);
    setIsOpenMenuId(-1);
    setIsOpenBankAccount(true);
  };

  const onCreateVirtualAccount = async () => {
    console.log('currencies: ', currencies);
    if (currencies.length == 0) return;
    await API.post('user/createVirtualAccount', { currencies: currencies })
      .then(async (res) => {
        console.log('res: ', res);
        await fetchData();
        setIsOpenVAModal(false);
        setCreatedVA(true);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message;
        console.log('err: ', err);
        if (err.status == 401) navigate('/login');

        setNotification({
          message: findMessage(msg),
          type: 'error',
        });
      });
  };
  const onChangeCurrency = (currency) => {
    if (currencies.includes(currency)) {
      setCurrencies(currencies.filter((i) => i !== currency));
    } else setCurrencies([...currencies, currency]);
  };
  const alreadyCreated = (currency) => {
    const selectedVirtualAccounts = virtualAccounts.filter(
      (item) => item.source_deposit_instructions.currency == currency
    );
    if (selectedVirtualAccounts.length) return true;
    return false;
  };

  return (
    <>
      <div className="cuentas-ola">
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        <SectionBanner
          id="accounts"
          title="Tus cuentas en otros países"
          text="Cuentas gratis en EE. UU., Europa, Brasil y México para recibir pagos como si fueras local. Lo que recibas llega directo a tu saldo."
        />

        <div className="cuentas-ola__head">
          <div>
            <p className="cuentas-ola__eyebrow">Mover</p>
            <p className="cuentas-ola__title">Cuentas Globales</p>
            <p className="cuentas-ola__sub">
              Recibe y envía en otros países como si fueras local
            </p>
          </div>
          <span className="space-x-3 flex">
            <IconButton
              disabled={false}
              bgColor="bg-[#092F7B]"
              icon={PlusIcon}
              text="Solicitar cuenta"
              textColor="text-[#FDFDFD]"
              onClick={() => setIsOpenVAModal(true)}
            />
          </span>
        </div>

        <div className="cuentas-ola__panel">
          <div className="cuentas-ola__balance">
            <div className="w-[46px] h-[46px] flex items-center justify-center">
              <img src={LogoIcon} alt="logo" className="w-[38px] h-[35px]" />
            </div>
            <div className="space-y-1 font-[poppins]">
              <p className="cuentas-ola__balance-lbl">
                Saldo acumulado
              </p>
              <p className="cuentas-ola__balance-val">
                <span>${balance}</span>
                <span className="cuentas-ola__balance-ccy">
                  USD
                </span>
              </p>
            </div>
          </div>

          <div className="cuentas-ola__grid">
            {virtualAccounts.map((item, idx) => {
              return (
                <div
                  key={idx}
                  className="cuentas-ola__card"
                >
                  <div className="cuentas-ola__card-inner">
                    <div className="flex items-center justify-center space-x-2 min-w-0">
                      <div className="w-[38px] h-[38px] flex items-center justify-center shrink-0">
                        <img
                          src={getFlag(item)}
                          alt="logo"
                          className="w-[38px] h-[38px]"
                        />
                      </div>
                      <div className="space-y-1 text-sm font-normal min-w-0 flex-1">
                        <p className="cuentas-ola__card-name">
                          {getRouteName(
                            item.source_deposit_instructions.currency
                          )}
                        </p>
                        <p className="cuentas-ola__card-num">
                          {getAccountNumber(item)}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => setIsOpenMenuId(idx)}
                      className="cuentas-ola__menu-btn"
                    >
                      <img
                        src={DotThreeVerticalIcon}
                        alt="Dot Three Vertical Icon"
                        className="w-6 h-6"
                      />
                    </button>
                  </div>

                  {isOpenMenuId === idx && (
                    <div
                      ref={bankItemRefs[idx]}
                      className="cuentas-ola__menu"
                    >
                      <ul className="py-1">
                        <li>
                          <button
                            onClick={() => openModal(idx)}
                          >
                            Ver datos
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isOpenBankAccount && (
        <BankAccountModal
          data={selectedBank}
          onClose={() => setIsOpenBankAccount(false)}
        />
      )}

      {isOpenVAModal && (
        <div>
          <div className="cuentas-ola__modal-backdrop"></div>
          <div className="cuentas-ola__modal-wrap">
            <div className="cuentas-ola__modal">
              <div className="cuentas-ola__modal-head">
                <p className="cuentas-ola__modal-title">
                  Solicitar cuenta
                </p>
                <button
                  onClick={() => {
                    setIsOpenVAModal(false);
                    setCurrencies([]);
                  }}
                  className="cuentas-ola__modal-close"
                >
                  <X className="w-5 h-5 cursor-pointer" />
                </button>
              </div>

              <div className="cuentas-ola__modal-body">
                <p className="cuentas-ola__sub">
                  Selecciona las cuentas que desear solicitar.
                </p>

                {VirtualAccountCurrencies.map((item, idx) => (
                  <div
                    key={idx}
                    className="cuentas-ola__option"
                  >
                    <input
                      disabled={alreadyCreated(item.currency) ? true : false}
                      type="checkbox"
                      onChange={() => onChangeCurrency(item.currency)}
                      className="form-checkbox h-5 w-5 cursor-pointer"
                    />

                    <img
                      src={item.flag}
                      alt={item.currency}
                      className="w-9.5 h-9.5 rounded-full"
                    />

                    <p className="cuentas-ola__option-lbl">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="cuentas-ola__modal-actions">
                <NormalButton
                  disabled={currencies && currencies.length ? false : true}
                  customClass={`px-14 ${
                    currencies && currencies.length
                      ? 'bg-[#092F7B] text-[#FDFDFD]'
                      : 'bg-[#E9EAEB] text-[#A4A7AE]'
                  }`}
                  text="Solicitar"
                  onClick={onCreateVirtualAccount}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {createdVA && (
        <div>
          <div className="cuentas-ola__modal-backdrop"></div>
          <div className="cuentas-ola__modal-wrap">
            <div className="cuentas-ola__modal cuentas-ola__modal--sm">
              <div className="space-y-2">
                <div className="py-4 space-y-2 flex flex-col items-center justify-center" style={{ borderBottom: '1px solid var(--fx-border)' }}>
                  <div className="cuentas-ola__success-ico">
                    <img
                      src={SuccessIcon}
                      alt="success icon"
                      className="w-6 h-6 rounded-full"
                    />
                  </div>
                  <p className="cuentas-ola__title" style={{ fontSize: '1.15rem' }}>
                    Cuentas solicitadas
                  </p>
                </div>

                <div className="cuentas-ola__sub space-y-5.5">
                  <p>Recibimos tu solicitud para las cuentas</p>

                  <ul className="list-disc list-inside rounded-md">
                    {currencies.map((item, idx) => (
                      <li key={idx} className="font-semibold" style={{ color: 'var(--fx-text)' }}>
                        Flowex Pay {getVACreationText(item)}
                      </li>
                    ))}
                  </ul>

                  <p>Se abrirán en el transcurso de la próximas 24 hs. </p>
                </div>
              </div>

              <div className="cuentas-ola__modal-actions" style={{ padding: '1rem 0 0' }}>
                <NormalButton
                  customClass="px-15 py-2 bg-[#092F7B] text-[#FDFDFD]"
                  text="Aceptar"
                  onClick={() => {
                    setCreatedVA(false);
                    setCurrencies([]);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
